<template>
    <div ref="wrapper" class="relative w-full">
        <button
            :id="triggerId"
            ref="trigger"
            type="button"
            role="combobox"
            :aria-controls="listboxId"
            :aria-expanded="open"
            aria-haspopup="listbox"
            :aria-labelledby="ariaLabelledby"
            :aria-label="ariaLabel"
            :aria-activedescendant="open && activeOption ? optionId(activeOption.value) : undefined"
            :disabled="disabled"
            class="flex w-full min-h-[48px] items-center justify-between gap-2 rounded-xs border bg-base-100 px-4 text-sm text-left transition-colors"
            :class="[
                open ? 'border-neutral' : 'border-base-content-subtle',
                disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:border-neutral'
            ]"
            data-cy="misc-select-trigger"
            @click="toggle"
            @keydown="onTriggerKeydown"
        >
            <span :class="selectedOption ? 'text-neutral' : 'text-base-content-subtle'">
                {{ selectedOption ? selectedOption.label : placeholder }}
            </span>
            <MiscIcon
                name="chevron-down"
                size="sm"
                class="shrink-0 text-base-content-subtle transition-transform"
                :class="{ 'rotate-180': open }"
            />
        </button>

        <ul
            v-show="open"
            :id="listboxId"
            ref="listbox"
            role="listbox"
            :aria-labelledby="ariaLabelledby"
            :aria-label="ariaLabel"
            class="absolute left-0 right-0 top-full z-20 mt-1 max-h-[280px] overflow-y-auto rounded-xs border border-base-content-subtle bg-base-100 py-1 shadow-lg"
            tabindex="-1"
        >
            <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/interactive-supports-focus -->
            <li
                v-for="option in options"
                :id="optionId(option.value)"
                :key="option.value"
                role="option"
                :aria-selected="option.value === modelValue"
                :aria-disabled="option.disabled || undefined"
                class="flex items-center gap-3 px-4 py-2.5 text-sm"
                :class="[
                    option.disabled
                        ? 'cursor-not-allowed text-base-content-subtle/40'
                        : 'cursor-pointer text-neutral',
                    { 'bg-base-200': !option.disabled && activeOption?.value === option.value }
                ]"
                data-cy="misc-select-option"
                @click="onOptionClick(option)"
                @mousemove="onOptionHover(option)"
            >
                <span
                    class="flex h-4 w-4 shrink-0 items-center justify-center rounded-full border"
                    :class="option.value === modelValue
                        ? 'border-[#077A50]'
                        : option.disabled ? 'border-base-content-subtle/40' : 'border-base-content-subtle'"
                >
                    <span
                        v-if="option.value === modelValue"
                        class="h-2 w-2 rounded-full bg-[#077A50]"
                    />
                </span>
                <span class="grow">{{ option.label }}</span>
            </li>
        </ul>
    </div>
</template>

<script setup lang="ts">
import { onClickOutside } from '@vueuse/core'

export interface MiscSelectOption {
    value: string
    label: string
    // Not selectable and visually muted (e.g. currently unavailable)
    disabled?: boolean
}

const props = withDefaults(defineProps<{
    options: MiscSelectOption[]
    modelValue?: string
    placeholder?: string
    disabled?: boolean
    // Pass one of ariaLabel / ariaLabelledby for an accessible name
    ariaLabel?: string
    ariaLabelledby?: string
}>(), {
    modelValue: undefined,
    placeholder: '',
    disabled: false,
    ariaLabel: undefined,
    ariaLabelledby: undefined
})

const emit = defineEmits<{
    (e: 'update:modelValue', value: string): void
}>()

const uid = useId()
const triggerId = `misc-select-trigger-${uid}`
const listboxId = `misc-select-listbox-${uid}`
const optionId = (value: string) => `misc-select-option-${uid}-${value}`

const wrapper = ref<HTMLElement>()
const trigger = ref<HTMLButtonElement>()
const listbox = ref<HTMLUListElement>()
const open = ref(false)
const activeValue = ref<string | undefined>(undefined)

const selectedOption = computed(() => props.options.find(option => option.value === props.modelValue))
const activeOption = computed(() => props.options.find(option => option.value === activeValue.value))
const enabledOptions = computed(() => props.options.filter(option => !option.disabled))

let typeahead = ''
let typeaheadTimer: ReturnType<typeof setTimeout> | undefined

onClickOutside(wrapper, () => closeList())

function toggle () {
    if (props.disabled) return
    if (open.value) {
        closeList()
    } else {
        openList()
    }
}

function openList () {
    if (props.disabled) return
    open.value = true
    // Start on the selected option, otherwise the first selectable one
    activeValue.value = selectedOption.value && !selectedOption.value.disabled
        ? selectedOption.value.value
        : enabledOptions.value[0]?.value
    scrollActiveIntoView()
}

function closeList () {
    open.value = false
}

function selectOption (option: MiscSelectOption) {
    if (option.disabled) return
    emit('update:modelValue', option.value)
    closeList()
    trigger.value?.focus()
}

function onOptionClick (option: MiscSelectOption) {
    selectOption(option)
}

function onOptionHover (option: MiscSelectOption) {
    if (!option.disabled) activeValue.value = option.value
}

function moveActive (direction: 1 | -1) {
    const list = enabledOptions.value
    if (list.length === 0) return
    const currentIndex = list.findIndex(option => option.value === activeValue.value)
    const nextIndex = currentIndex === -1
        ? (direction === 1 ? 0 : list.length - 1)
        : (currentIndex + direction + list.length) % list.length
    activeValue.value = list[nextIndex]?.value
    scrollActiveIntoView()
}

function moveTo (position: 'first' | 'last') {
    const list = enabledOptions.value
    activeValue.value = position === 'first' ? list[0]?.value : list[list.length - 1]?.value
    scrollActiveIntoView()
}

function scrollActiveIntoView () {
    nextTick(() => {
        if (!activeValue.value) return
        const el = listbox.value?.querySelector(`#${CSS.escape(optionId(activeValue.value))}`)
        el?.scrollIntoView({ block: 'nearest' })
    })
}

function handleTypeahead (char: string) {
    if (typeaheadTimer) clearTimeout(typeaheadTimer)
    typeahead += char.toLowerCase()
    typeaheadTimer = setTimeout(() => { typeahead = '' }, 500)

    const match = enabledOptions.value.find(option => option.label.toLowerCase().startsWith(typeahead))
    if (match) {
        activeValue.value = match.value
        if (!open.value) emit('update:modelValue', match.value)
        else scrollActiveIntoView()
    }
}

function onTriggerKeydown (event: KeyboardEvent) {
    if (props.disabled) return

    switch (event.key) {
    case 'ArrowDown':
        event.preventDefault()
        if (open.value) {
            moveActive(1)
        } else {
            openList()
        }
        break
    case 'ArrowUp':
        event.preventDefault()
        if (open.value) {
            moveActive(-1)
        } else {
            openList()
        }
        break
    case 'Home':
        if (open.value) {
            event.preventDefault()
            moveTo('first')
        }
        break
    case 'End':
        if (open.value) {
            event.preventDefault()
            moveTo('last')
        }
        break
    case 'Enter':
    case ' ':
        event.preventDefault()
        if (open.value && activeOption.value) {
            selectOption(activeOption.value)
        } else {
            openList()
        }
        break
    case 'Escape':
        if (open.value) {
            event.preventDefault()
            closeList()
        }
        break
    case 'Tab':
        closeList()
        break
    default:
        if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
            handleTypeahead(event.key)
        }
    }
}

onBeforeUnmount(() => {
    if (typeaheadTimer) clearTimeout(typeaheadTimer)
})
</script>
